import React, { useEffect, useRef, useState } from "react";
import InputField from "../shared-components/InputField";

import { classNames, dropdownItemSortEval } from "../utils";
import { ButtonColorTypes, ButtonTypes } from "../shared-components/Button";
import Button from "../shared-components/Button";
import { ChecktypeData, useReportsWizard } from "./ReportsWizardProvider";
import {
  checktypeAfterThreeSixtySelection,
  reconcileChecktypeForCompany,
  resolveBehaviorKitId,
} from "./ReportsWizardUtil";
import CheckTypesMulti from "../persons/form-multi/CheckTypesMulti";
import { BlueprintMinimalInfo } from "../api/Blueprints";
import { REPORT_TYPES } from "../api/Reports";
import { BehaviorKit, CompanyWithBlueprintInfo } from "../api/Companies";
import DropdownCombo, {
  DropdownItem,
} from "../shared-components/DropdownCombo";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useFamaFlags } from "../api/FamaFlags";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import ReactTooltip from "react-tooltip";

type Props = {};

export const ReportsWizardChecktype = (props: Props) => {
  const {
    nextStep,
    companyQuery,
    blueprintsQuery,
    previousStep,
    setChecktypeInfo,
    getChecktypeInfo,
    companyWithBlueprintQuery,
    behaviorKitQuery,
    is_rerun,
    checktypeBoundCompanyUUID,
    setChecktypeBoundCompanyUUID,
  } = useReportsWizard();
  const [state, setState] = useState<{
    internal_qa?: boolean;
    client_qa?: boolean;
    requisitionNumber: string;
    behavior_kit: string;
    selectedMisconductChecktype: string;
    selectedThreeSixtyChecktype: string;
    recurring_checks: boolean;
    frequency: number;
  }>({
    internal_qa: undefined,
    client_qa: undefined,
    requisitionNumber: "",
    behavior_kit: "",
    selectedMisconductChecktype: "",
    selectedThreeSixtyChecktype: "",
    recurring_checks: false,
    frequency: 0,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Sync guard: React state alone cannot block same-tick double-clicks before re-render.
  const isSubmittingRef = useRef(false);
  const isMountedRef = useRef(true);
  const { showBehaviorKit: showBehaviorKitFlag } = useFamaFlags();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { canInternalQaView } = usePermissionValidator();

  const [behaviorKitItems, setBehaviorKitItems] = useState<DropdownItem[]>([]);
  const [selectedBehaviorKit, setSelectedBehaviorKit] = useState<string>("");
  const [blueprintItems, setBlueprintItems] = useState<BlueprintMinimalInfo[]>(
    []
  );
  const [selectedMisconductChecks, setSelectedMisconductChecks] = useState<
    BlueprintMinimalInfo[]
  >([]);
  const [selectedThreeSixtyChecks, setSelectedThreeSixtyChecks] = useState<
    BlueprintMinimalInfo[]
  >([]);

  const createBehaviorsKitItems = (
    company: CompanyWithBlueprintInfo | null,
    behaviorKList: BehaviorKit[] | undefined
  ) => {
    if (behaviorKList && behaviorKList.length > 0) {
      const list = behaviorKList;
      const items: DropdownItem[] = list.map((element) => {
        return {
          id: element.external_id ?? "",
          label: element.name,
        };
      });
      items.push({ id: "NULL", label: "General" });
      items.sort(dropdownItemSortEval);

      let default_bk = "null";
      if (company?.default_behavior_kit) {
        default_bk = company?.default_behavior_kit.toLowerCase();
      }
      const sort_by_default_bk = (a: DropdownItem, b: DropdownItem) => {
        if (a.id.toLowerCase() === default_bk) {
          return -1;
        }
        return 0;
      };
      items.sort(sort_by_default_bk);
      return items;
    } else {
      const emptyList: DropdownItem[] = [{ id: "NULL", label: "General" }];
      return emptyList;
    }
  };

  useEffect(() => {
    let checktypeError = false;
    let frequencyError = false;
    if (
      state.selectedThreeSixtyChecktype.length === 0 &&
      state.selectedMisconductChecktype.length === 0
    ) {
      checktypeError = true;
    }

    if (
      state.selectedMisconductChecktype.length > 0 &&
      state.recurring_checks &&
      state.frequency < 1
    ) {
      frequencyError = true;
    }

    const hasErrors = checktypeError || frequencyError;
    setIsValid(!hasErrors);
  }, [
    state.selectedThreeSixtyChecktype,
    state.selectedMisconductChecktype,
    state.recurring_checks,
    state.frequency,
  ]);
  const companiesWithBlueprint = companyWithBlueprintQuery.data;
  const behaviorKitList = behaviorKitQuery.data;

  useEffect(() => {
    let company: CompanyWithBlueprintInfo | null = null;
    let items: BlueprintMinimalInfo[] = [];
    const stored = getChecktypeInfo();
    let checktypeInfo: ChecktypeData = {
      selectedMisconductChecktype: stored?.selectedMisconductChecktype ?? "",
      selectedThreeSixtyChecktype: stored?.selectedThreeSixtyChecktype ?? "",
      selectedBehaviorKit: stored?.selectedBehaviorKit ?? "",
      requisitionNumber: stored?.requisitionNumber ?? "",
      checkedPulseReport: stored?.checkedPulseReport ?? false,
      pulseReportMonths: stored?.pulseReportMonths ?? 0,
      internalQa: stored?.internalQa,
      clientQa: stored?.clientQa,
    };
    if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
      company = companiesWithBlueprint[0];
      items = company.blueprintsInfo ?? [];
      setBlueprintItems(items);
    }
    const behaviorKitItemsList = createBehaviorsKitItems(
      company,
      behaviorKitList
    );
    setBehaviorKitItems(behaviorKitItemsList);
    const kitsLoaded =
      !behaviorKitQuery.isLoading && behaviorKitList !== undefined;
    const kitIds = kitsLoaded
      ? behaviorKitItemsList.map((item) => item.id)
      : undefined;

    if (company && company.company_uuid !== checktypeBoundCompanyUUID) {
      // First company seeds QA defaults. A later company change clears check type, Pulse, and IQA/CQA.
      checktypeInfo = reconcileChecktypeForCompany(
        checktypeInfo,
        company,
        kitIds,
        { initialCompany: checktypeBoundCompanyUUID == null }
      );
      setChecktypeInfo(checktypeInfo);
      setChecktypeBoundCompanyUUID(company.company_uuid);
    } else if (company && kitsLoaded && kitIds) {
      // Same company: keep explicit IQA/CQA and Pulse. Only replace a kit this company does not offer.
      const resolvedKit = resolveBehaviorKitId(
        checktypeInfo.selectedBehaviorKit,
        kitIds
      );
      if (resolvedKit !== checktypeInfo.selectedBehaviorKit) {
        checktypeInfo = {
          ...checktypeInfo,
          selectedBehaviorKit: resolvedKit,
        };
        setChecktypeInfo(checktypeInfo);
      }
    }

    const foundMisconductChecks = findSelectionPerList(
      items,
      checktypeInfo,
      "selectedMisconductChecktype"
    );
    const foundThreeSixtyChecks = findSelectionPerList(
      items,
      checktypeInfo,
      "selectedThreeSixtyChecktype"
    );
    const foundBehaviorKit = checktypeInfo.selectedBehaviorKit
      ? checktypeInfo.selectedBehaviorKit
      : behaviorKitItemsList[0]?.id ?? "";

    setSelectedMisconductChecks(foundMisconductChecks);
    setSelectedThreeSixtyChecks(foundThreeSixtyChecks);
    setSelectedBehaviorKit(foundBehaviorKit);

    setState((prev) => ({
      ...prev,
      internal_qa: checktypeInfo.internalQa ?? false,
      client_qa: checktypeInfo.clientQa ?? false,
      frequency: checktypeInfo.pulseReportMonths ?? 0,
      recurring_checks: checktypeInfo.checkedPulseReport ?? false,
      selectedMisconductChecktype:
        foundMisconductChecks[0]?.blueprint_uuid ?? "",
      selectedThreeSixtyChecktype:
        foundThreeSixtyChecks[0]?.blueprint_uuid ?? "",
      behavior_kit: foundBehaviorKit,
      requisitionNumber: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyQuery.data,
    blueprintsQuery.data,
    companiesWithBlueprint,
    behaviorKitList,
    behaviorKitQuery.isLoading,
    checktypeBoundCompanyUUID,
  ]);

  const onSourceChange = (
    active: boolean,
    bp_uuid: string,
    type: REPORT_TYPES
  ) => {
    let selectedChecks = selectedMisconductChecks;
    if (type === REPORT_TYPES.THREE_SIXTY) {
      selectedChecks = selectedThreeSixtyChecks;
    }
    // always remove requested item first to prevent duplicates
    let newSources = selectedChecks.filter((source) => {
      return source.blueprint_uuid !== bp_uuid;
    });
    if (active) {
      const found = blueprintItems.find((blueprint) => {
        return blueprint.blueprint_uuid === bp_uuid;
      });
      if (found) {
        // remove other checks of same type as found
        newSources = newSources.filter(
          (item) => item.report_type !== found.report_type
        );
        newSources.push(found);
      }
    }

    if (type === REPORT_TYPES.MISCONDUCT) {
      setSelectedMisconductChecks(newSources);
      setSelectedThreeSixtyChecks([]);
      const misconductCleared = newSources.length === 0;
      if (misconductCleared) {
        setState((prev) => ({
          ...prev,
          selectedMisconductChecktype: "",
          selectedThreeSixtyChecktype: "",
          recurring_checks: false,
          frequency: 0,
        }));
      }
      handleUpdateFields([
        {
          field: "selectedMisconductChecktype",
          value: newSources[0]?.blueprint_uuid || "",
        },
        { field: "selectedThreeSixtyChecktype", value: "" },
        ...(misconductCleared
          ? [
              { field: "checkedPulseReport" as const, value: false },
              { field: "pulseReportMonths" as const, value: 0 },
            ]
          : []),
      ]);
    } else if (type === REPORT_TYPES.THREE_SIXTY) {
      const blueprintUuid = newSources[0]?.blueprint_uuid || "";
      const previous = getChecktypeInfo();
      if (previous) {
        setChecktypeInfo(
          checktypeAfterThreeSixtySelection(previous, blueprintUuid)
        );
      }
      setSelectedMisconductChecks([]);
      setSelectedThreeSixtyChecks(newSources);
      setState((prev) => ({
        ...prev,
        selectedMisconductChecktype: "",
        selectedThreeSixtyChecktype: blueprintUuid,
        internal_qa: false,
        client_qa: false,
        recurring_checks: false,
        frequency: 0,
      }));
    }
  };

  function hasCheckTypeForBehaviorKit() {
    return (
      selectedMisconductChecks.length > 0 || selectedThreeSixtyChecks.length > 0
    );
  }

  function handleUpdateFields(
    fields: { field: keyof ChecktypeData; value: string | boolean | number }[]
  ) {
    let newStateField: Record<string, string | boolean | number> = {};
    fields.forEach(({ field, value }) => {
      newStateField[field] = value;
    });
    setState((prevState) => ({
      ...prevState,
      ...newStateField,
    }));
    const checktypeInfo = getChecktypeInfo();
    if (checktypeInfo) {
      const newChecktypeInfo: ChecktypeData = { ...checktypeInfo };
      fields.forEach(({ field, value }) => {
        switch (field) {
          case "checkedPulseReport":
          case "internalQa":
          case "clientQa":
            newChecktypeInfo[field] = value as boolean;
            break;
          case "pulseReportMonths":
            newChecktypeInfo.pulseReportMonths = +value;
            break;
          default:
            newChecktypeInfo[field] = value as string;
        }
      });
      setChecktypeInfo(newChecktypeInfo);
    }
  }

  const handleNextStep = async () => {
    setIsDirty(true);
    if (!isValid) {
      return;
    }
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // May await createPersonEmpty via ensurePersonAvailable before advancing.
      await nextStep(isValid, true);
    } catch {
      // Stay on this step so the user can retry; error surfaces elsewhere if any.
    } finally {
      isSubmittingRef.current = false;
      // Successful advance unmounts this step; only reset UI if we are still here.
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  function singleTypeCheckTypes(
    blueprintItems: BlueprintMinimalInfo[],
    reportType: REPORT_TYPES
  ) {
    return blueprintItems.filter((blueprint) => {
      return blueprint.report_type === reportType;
    });
  }

  const misconductChecks = singleTypeCheckTypes(
    blueprintItems,
    REPORT_TYPES.MISCONDUCT
  );
  const threeSixtyChecks = singleTypeCheckTypes(
    blueprintItems,
    REPORT_TYPES.THREE_SIXTY
  );
  function onQaChange(e: React.ChangeEvent<HTMLInputElement>) {
    setState({
      ...state,
      [e.target.name]: e.target.checked,
    });
    if (e.target.name === "internal_qa") {
      handleUpdateFields([{ field: "internalQa", value: e.target.checked }]);
    } else {
      handleUpdateFields([{ field: "clientQa", value: e.target.checked }]);
    }
  }

  function onRecurringChecksChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.checked;
    setState({
      ...state,
      recurring_checks: value,
      frequency: 0,
    });
    handleUpdateFields([
      { field: "checkedPulseReport", value: value },
      { field: "pulseReportMonths", value: 0 },
    ]);
  }

  const findSelectionPerList = (
    items: BlueprintMinimalInfo[],
    checktypeInfo: ChecktypeData | null,
    checkTypeKey: "selectedMisconductChecktype" | "selectedThreeSixtyChecktype"
  ) => {
    if (checktypeInfo?.[checkTypeKey]) {
      const found = items.find(
        (item) => item.blueprint_uuid === checktypeInfo[checkTypeKey]
      );
      return found ? [found] : [];
    }
    return [];
  };

  const errorChecktype =
    state.selectedThreeSixtyChecktype.length === 0 &&
    state.selectedMisconductChecktype.length === 0;

  const errorFrequency =
    state.selectedMisconductChecktype.length > 0 &&
    state.recurring_checks &&
    state.frequency < 1;

  const isLoadingCompanyBlueprints =
    companyWithBlueprintQuery.isLoading || behaviorKitQuery.isLoading;
  return (
    <div>
      <div className="text-lg font-bold text-darkBlue">Check Types</div>
      <div className="text-xs text-gray-500 mt-4">
        Please select at least one check type.
      </div>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <div className="flex gap-6 pt-4">
          {isLoadingCompanyBlueprints && <LoadingSpinner />}
          {!isLoadingCompanyBlueprints && (
            <>
              <div className="flex-1 flex flex-col">
                {misconductChecks.length > 0 && (
                  <>
                    <span className="block mb-2 text-sm font-medium text-gray-600">
                      Misconduct Check Types
                    </span>
                    <div className="bg-white p-4 border border-gray-300 rounded-lg flex-1">
                      <CheckTypesMulti
                        blueprintItems={misconductChecks}
                        selections={selectedMisconductChecks}
                        onChange={(active, bp_uuid) =>
                          onSourceChange(
                            active,
                            bp_uuid,
                            REPORT_TYPES.MISCONDUCT
                          )
                        }
                        noHeader={true}
                      />
                    </div>
                  </>
                )}
                {threeSixtyChecks.length > 0 && (
                  <>
                    <span
                      className={classNames(
                        "block mb-2 text-sm font-medium text-gray-600",
                        misconductChecks.length > 0 ? "mt-6" : ""
                      )}
                    >
                      360 Check Types
                    </span>
                    <div className="bg-white p-4 border border-gray-300 rounded-lg flex-1">
                      <CheckTypesMulti
                        blueprintItems={threeSixtyChecks}
                        selections={selectedThreeSixtyChecks}
                        onChange={(active, bp_uuid) =>
                          onSourceChange(
                            active,
                            bp_uuid,
                            REPORT_TYPES.THREE_SIXTY
                          )
                        }
                        noHeader={true}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        {isDirty && errorChecktype && (
          <div className="text-red-500 text-sm mt-2">
            At least one check type is required.
          </div>
        )}
        <div className="flex gap-6">
          <div className="flex-1">
            {behaviorKitItems &&
              showBehaviorKitFlag &&
              hasCheckTypeForBehaviorKit() && (
                <DropdownCombo
                  containerClassName="mt-8"
                  buttonContainerClassName="w-full"
                  items={behaviorKitItems}
                  initial={behaviorKitItems.find(
                    (item) => item.id === selectedBehaviorKit
                  )}
                  onChange={(e) => {
                    handleUpdateFields([
                      { field: "selectedBehaviorKit", value: e.id },
                    ]);
                    setSelectedBehaviorKit(e.id);
                  }}
                  label={"Behavior Kit*"}
                  name="selected-behavior-kit"
                  icon={
                    <InformationCircleIcon
                      height={24}
                      width={24}
                      color="grey"
                    />
                  }
                  tooltip="Behavior kits are the configurable filters that companies can use to set behavior flags, lookback periods, and keywords for any given check type.
                    The General Behavior Kit utilizes configured settings derived from the selected Check Type."
                ></DropdownCombo>
              )}
          </div>
          <div className="flex-1">
            {selectedMisconductChecks.length > 0 &&
              companiesWithBlueprint &&
              companiesWithBlueprint.length > 0 &&
              (companiesWithBlueprint[0]?.client_qa ||
                companiesWithBlueprint[0]?.internal_qa) && (
                <div className="flex-1 mt-8">
                  <span className="block mb-2 text-sm font-medium text-gray-600">
                    QA
                  </span>
                  <div className="bg-white px-4 py-2.5 border border-gray-300 rounded-lg h-full flex-1">
                    {companiesWithBlueprint[0]?.internal_qa &&
                      canInternalQaView && (
                        <LabeledCheckbox
                          key="internal_qa"
                          id="internal_qa"
                          label="Internal Investigator"
                          name="internal_qa"
                          preserveLabelCasing={true}
                          onChange={onQaChange}
                          disabled={is_rerun}
                          checked={state.internal_qa}
                          classes="mb-3"
                        ></LabeledCheckbox>
                      )}
                    {companiesWithBlueprint[0]?.client_qa && (
                      <LabeledCheckbox
                        key="client_qa"
                        id="client_qa"
                        label="Client Investigator"
                        name="client_qa"
                        onChange={onQaChange}
                        disabled={is_rerun}
                        checked={state.client_qa}
                      ></LabeledCheckbox>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      {selectedMisconductChecks.length > 0 && (
        <>
          <div className="text-lg font-bold text-darkBlue pt-8">
            Employee Screening Subscription
          </div>
          <div className="flex gap-6 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
            <div className="flex-1">
              <span className="block mb-2 text-sm font-medium text-gray-600">
                Select the box to set up employee rescreening
              </span>
              <div className="bg-white px-4 py-2.5 border border-gray-300 rounded-lg flex-1">
                <LabeledCheckbox
                  key="recurring_checks"
                  id="recurring_checks"
                  label="Add Automated Employee Screening"
                  name="recurring_checks"
                  preserveLabelCasing={true}
                  onChange={onRecurringChecksChange}
                  disabled={is_rerun}
                  checked={state.recurring_checks}
                ></LabeledCheckbox>
              </div>
            </div>
            <div className="flex-1">
              {state.recurring_checks && (
                <>
                  <span className="block mb-1 text-sm font-medium text-gray-600">
                    Frequency
                    <span data-tip data-for={"frequency-tooltip"}>
                      <InformationCircleIcon className="inline-flex w-6 text-gray-500" />
                    </span>
                  </span>
                  <ReactTooltip
                    className="hover:visible:opacity-1 w-96"
                    delayHide={300}
                    effect="solid"
                    html={false}
                    place="right"
                    id={"frequency-tooltip"}
                  >
                    Enter the frequency of checks in Months.
                  </ReactTooltip>

                  <div className="flex-1 flex gap-4 items-center">
                    <InputField
                      value={state.frequency}
                      handleChange={(e) => {
                        const value = !isNaN(Number(e.target.value))
                          ? Number(e.target.value)
                          : 0;
                        setState({
                          ...state,
                          frequency: value,
                        });
                        handleUpdateFields([
                          { field: "pulseReportMonths", value: value },
                        ]);
                      }}
                      classes="w-16"
                      name="frequency"
                      disabled={is_rerun}
                      className={classNames(
                        isDirty && errorFrequency ? "border-red-500" : "",
                        "text-center w-16"
                      )}
                      placeholder=""
                    />
                    <span className="font-thin">Months</span>
                  </div>
                </>
              )}
            </div>
          </div>
          {isDirty && errorFrequency && (
            <div className="text-red-500 text-sm mt-2">
              Frequency must be greater than 0.
            </div>
          )}
        </>
      )}
      <div className="flex justify-between pt-8 items-center">
        <Button
          type={ButtonTypes.default}
          text={"Previous Step"}
          colorType={ButtonColorTypes.alternate}
          onClick={() => previousStep()}
          disabled={isSubmitting}
        />
        {isSubmitting && <LoadingSpinner />}
        <Button
          text={"Next Step"}
          colorType={ButtonColorTypes.custom}
          isRoundedFull={true}
          className="bg-orange-500 text-white rounded-full ml-auto w-56"
          onClick={handleNextStep}
          disabled={isSubmitting || (isDirty && !isValid)}
        />
      </div>
    </div>
  );
};
